import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { epostaMerkez } from "@/lib/api";

const EMPTY = {
    ad: "",
    email_domains: "",
    subject_keywords: "",
    body_keywords: "",
    regex_patterns: "{}",
    oncelik: 0,
    aktif: true,
};

export default function EpostaKurallarPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await epostaMerkez.sirketKurallari();
            setItems(data.items || []);
        } catch {
            toast.error("Kurallar yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    function rowToForm(k) {
        return {
            ad: k.ad || "",
            email_domains: (k.email_domains || []).join(", "),
            subject_keywords: (k.subject_keywords || []).join(", "),
            body_keywords: (k.body_keywords || []).join(", "),
            regex_patterns: JSON.stringify(k.regex_patterns || {}, null, 2),
            oncelik: k.oncelik || 0,
            aktif: k.aktif !== false,
        };
    }

    function formToPayload() {
        const split = (s) =>
            s
                .split(/[,;\n]/)
                .map((x) => x.trim())
                .filter(Boolean);
        let regex = {};
        try {
            regex = JSON.parse(form.regex_patterns || "{}");
        } catch {
            throw new Error("Regex JSON geçersiz");
        }
        return {
            ad: form.ad.trim(),
            email_domains: split(form.email_domains),
            subject_keywords: split(form.subject_keywords),
            body_keywords: split(form.body_keywords),
            regex_patterns: regex,
            oncelik: Number(form.oncelik) || 0,
            aktif: form.aktif,
        };
    }

    async function handleSubmit(e) {
        e.preventDefault();
        try {
            const payload = formToPayload();
            if (editId) {
                await epostaMerkez.sirketKuralGuncelle(editId, payload);
                toast.success("Kural güncellendi.");
            } else {
                await epostaMerkez.sirketKuralOlustur(payload);
                toast.success("Kural oluşturuldu.");
            }
            setForm(EMPTY);
            setEditId(null);
            load();
        } catch (err) {
            toast.error(err.message || "Kayıt başarısız.");
        }
    }

    async function handleDelete(id) {
        if (!window.confirm("Kural silinsin mi?")) return;
        try {
            await epostaMerkez.sirketKuralSil(id);
            toast.success("Silindi.");
            if (editId === id) {
                setEditId(null);
                setForm(EMPTY);
            }
            load();
        } catch {
            toast.error("Silinemedi.");
        }
    }

    return (
        <div className="grid lg:grid-cols-2 gap-4">
            <form onSubmit={handleSubmit} className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4 space-y-2">
                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    {editId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {editId ? "Kural düzenle" : "Yeni şirket kuralı"}
                </h3>
                <input
                    placeholder="Şirket adı"
                    value={form.ad}
                    onChange={(e) => setForm({ ...form, ad: e.target.value })}
                    className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                    required
                />
                <input
                    placeholder="E-posta domainleri (virgülle)"
                    value={form.email_domains}
                    onChange={(e) => setForm({ ...form, email_domains: e.target.value })}
                    className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                />
                <input
                    placeholder="Konu anahtar kelimeleri"
                    value={form.subject_keywords}
                    onChange={(e) => setForm({ ...form, subject_keywords: e.target.value })}
                    className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                />
                <input
                    placeholder="Gövde anahtar kelimeleri"
                    value={form.body_keywords}
                    onChange={(e) => setForm({ ...form, body_keywords: e.target.value })}
                    className="w-full h-9 rounded border border-white/10 bg-black/30 px-3 text-xs"
                />
                <textarea
                    placeholder='Regex JSON örn: {"talep_no": "Talep[\\s:]+(\\d+)"}'
                    value={form.regex_patterns}
                    onChange={(e) => setForm({ ...form, regex_patterns: e.target.value })}
                    rows={4}
                    className="w-full rounded border border-white/10 bg-black/30 p-2 text-xs font-mono"
                />
                <div className="flex gap-2 items-center">
                    <input
                        type="number"
                        value={form.oncelik}
                        onChange={(e) => setForm({ ...form, oncelik: e.target.value })}
                        className="w-20 h-8 rounded border border-white/10 bg-black/30 px-2 text-xs"
                    />
                    <label className="text-xs text-slate-400 flex items-center gap-1">
                        <input type="checkbox" checked={form.aktif} onChange={(e) => setForm({ ...form, aktif: e.target.checked })} />
                        Aktif
                    </label>
                </div>
                <div className="flex gap-2">
                    <button type="submit" className="h-8 px-4 rounded bg-indigo-600 text-white text-xs font-semibold">
                        {editId ? "Güncelle" : "Oluştur"}
                    </button>
                    {editId && (
                        <button
                            type="button"
                            onClick={() => {
                                setEditId(null);
                                setForm(EMPTY);
                            }}
                            className="h-8 px-3 rounded border border-white/10 text-xs text-slate-400"
                        >
                            İptal
                        </button>
                    )}
                </div>
            </form>

            <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                <h3 className="text-sm font-semibold text-slate-200 mb-3">Kayıtlı kurallar ({items.length})</h3>
                {loading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                ) : (
                    <div className="space-y-2 max-h-[520px] overflow-y-auto">
                        {items.map((k) => (
                            <div key={k.id} className="rounded border border-white/10 p-3 text-xs">
                                <div className="flex justify-between gap-2">
                                    <span className="font-medium text-slate-200">{k.ad}</span>
                                    <div className="flex gap-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditId(k.id);
                                                setForm(rowToForm(k));
                                            }}
                                            className="text-sky-400 text-[10px]"
                                        >
                                            Düzenle
                                        </button>
                                        <button type="button" onClick={() => handleDelete(k.id)} className="text-rose-400">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-slate-500 mt-1">
                                    {(k.email_domains || []).join(", ")} · öncelik {k.oncelik}
                                    {k.aktif === false && " · pasif"}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
